import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Eyebrow, Lines, Reveal } from "./Motion";

const STATS = [
  { v: 15, s: " yrs", l: "Facilities Management at ETSU" },
  { t: "B.S.", l: "Construction Management" },
  { t: "2022", l: "Established" },
  { t: "$0", l: "Cost for an estimate" },
];

const FAQ = [
  ["What kind of projects do you take on?", "New construction decks, covered porches, siding and exterior upgrades like windows, doors, trim and railings."],
  ["How long have you been in business?", "Matt Newland started Newland Construction in 2022, after a lifetime around construction and 15 years in Facilities Management at ETSU."],
  ["Where do you work?", "Across the Tri-Cities region: Johnson City, Kingsport, Bristol and surrounding communities like Elizabethton, Jonesborough, Gray, Blountville and Piney Flats."],
  ["Are estimates really free?", "Yes. We'll walk your property, talk through options and send a clear written estimate at no cost."],
  ["Who will I actually be working with?", "Matt Newland, the owner. He walks your site, prices your project and stays involved through the build."],
  ["How long does a typical deck take?", "It depends on size, materials and permitting, but we'll give you a realistic schedule with your estimate and keep you updated the whole way."],
];

const Count = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const c = animate(0, to, { duration: 2, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setN(Math.round(v)) });
    return () => c.stop();
  }, [inView, to]);
  return <span ref={ref}>{n}{suffix}</span>;
};

export const Proof = () => (
  <section data-testid="section-proof" className="relative bg-[#0A0A0A] px-5 py-28 md:px-10 md:py-40">
    <div className="mx-auto max-w-[1500px]">
      <div className="grid grid-cols-2 border-l border-t border-white/[0.08] lg:grid-cols-4">
        {STATS.map((s, i) => (
          <div key={s.l} data-testid={`stat-${i}`} className="border-b border-r border-white/[0.08] p-6 md:p-10">
            <div className="font-display text-5xl font-extrabold tracking-tighter text-neutral-50 md:text-7xl">{s.v ? <Count to={s.v} suffix={s.s} /> : s.t}</div>
            <div className="mt-3 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500">{s.l}</div>
          </div>
        ))}
      </div>
      <div className="mt-28 grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Eyebrow>Questions</Eyebrow>
          <h2 className="mt-6 font-display text-4xl font-bold leading-[1] tracking-tighter text-neutral-50 md:text-5xl">
            <Lines lines={["Good questions.", <span key="q" className="font-serif font-normal italic text-neutral-400">Straight answers.</span>]} />
          </h2>
          <button data-testid="faq-ask-ai-button" onClick={() => window.dispatchEvent(new Event("open-chat"))}
            className="mt-8 inline-flex items-center gap-3 rounded-full border border-white/15 px-5 py-3 text-sm text-neutral-200 transition-colors hover:border-white/60">
            <MessageCircle className="h-4 w-4" /> Ask our AI assistant anything
          </button>
        </div>
        <Reveal className="lg:col-span-7 lg:col-start-6">
          <Accordion type="single" collapsible className="border-t border-white/[0.08]">
            {FAQ.map(([q, a], i) => (
              <AccordionItem key={q} value={`q${i}`} className="border-white/[0.08]">
                <AccordionTrigger data-testid={`faq-question-${i}`} className="py-6 text-left font-display text-xl font-bold tracking-tight text-neutral-100 hover:no-underline md:text-2xl">{q}</AccordionTrigger>
                <AccordionContent className="pb-6 text-base leading-relaxed text-neutral-400">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </div>
  </section>
);
